import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { advisorOptions } from '../../data/advisor';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct } from '../../utils/products';
export function BatteryAdvisor() {
  const {
    addItem
  } = useCart();
  const [selected, setSelected] = useState<string>(advisorOptions[0].id);
  const option = advisorOptions.find(o => o.id === selected)!;
  const match = getProduct(option.productId)!;
  const isSystem = match.template === 'system';
  return <section id="advisor" aria-labelledby="advisor-heading" className="mx-auto max-w-7xl scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8 lg:py-16" data-id="element-70" data-mp="3gepd78czri56">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-12" data-id="element-71" data-mp="31iznp08lkmvp">
        <div className="lg:col-span-4" data-id="element-72" data-mp="36h15lhuaeg5l">
          <h2 id="advisor-heading" className="text-2xl font-bold tracking-tight text-ink sm:text-[28px]" data-id="element-73" data-mp="36fzsz8xmhllr">
            Not sure which battery fits?
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-74" data-mp="t7xjeuriiozd">
            Tell us what you want to power and we’ll point you to the right system. Prefer to talk it through? Our engineers
            answer within minutes.
          </p>
          <a href="tel:+31851234567" className="mt-4 inline-block text-sm font-semibold text-ink underline underline-offset-4" data-id="element-75" data-mp="1337mzmxk74f9">
            Call 085 123 4567
          </a>
        </div>

        <div className="grid gap-4 lg:col-span-8 lg:grid-cols-2" data-id="element-76" data-mp="ai7nu5fxdz6e">
          <fieldset data-id="element-77" data-mp="o8b4mgw1d0og">
            <legend className="mb-3 text-sm font-semibold text-ink" data-id="element-78" data-mp="o8unbztift3j">What do you want to power?</legend>
            <div className="space-y-2.5" data-id="element-79" data-mp="jfruxv0gypox">
              {advisorOptions.map(o => {
              const isActive = o.id === selected;
              return <label key={o.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border bg-white p-4 transition-colors duration-150 ${isActive ? 'border-ink' : 'border-line hover:border-ink/30'}`} data-id="element-80" data-mp="3nwchb3vum19w">
                    <input type="radio" name="advisor" value={o.id} checked={isActive} onChange={() => setSelected(o.id)} className="sr-only" data-id="element-81" data-mp="20m191wrb34j0" />
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${isActive ? 'border-ink bg-ink' : 'border-ink/25'}`} aria-hidden="true" data-id="element-82" data-mp="25lnlw5382ira">
                      {isActive && <CheckIcon className="h-3 w-3 text-volt" strokeWidth={3} data-id="element-83" data-mp="1htfergw1rm0j" />}
                    </span>
                    <span data-id="element-84" data-mp="1muwgfemcvci6">
                      <span className="block text-sm font-semibold text-ink" data-id="element-85" data-mp="1mqyxaszs2kya">{o.label}</span>
                      <span className="block text-xs text-muted" data-id="element-86" data-mp="2tbqc0fnkten9">{o.description}</span>
                    </span>
                  </label>;
            })}
            </div>
          </fieldset>

          <div className="flex flex-col rounded-xl bg-ink p-5 text-white" aria-live="polite" data-id="element-87" data-mp="hm20h4csne9u">
            <p className="text-xs font-semibold text-volt" data-id="element-88" data-mp="hoei1gen3mmd">Our recommendation</p>
            <AnimatePresence mode="wait" data-id="element-89" data-mp="cth6a1pz6z5s">
              <motion.div key={match.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-3 flex gap-4" data-id="element-90" data-mp="7wqtpoddqlp4">
                <img src={match.image} alt="" className="h-24 w-24 shrink-0 rounded-lg bg-surface object-cover" data-id="element-91" data-mp="3q2n383eu1b4i" />
                <div className="min-w-0" data-id="element-92" data-mp="3l4bvby83laox">
                  <h3 className="text-base font-semibold leading-snug" data-id="element-93" data-mp="1oa8nnazrgir9">
                    <Link to={`/product/${match.id}`} className="hover:underline" data-id="element-94" data-mp="1o8e9qgmwpqwo">
                      {match.name}
                    </Link>
                  </h3>
                  <p className="mt-1 text-xs text-white/60" data-id="element-95" data-mp="gzautibjxpk8">{match.specs.join(' · ')}</p>
                  <p className="mt-2 text-xl font-bold tracking-tight" data-id="element-96" data-mp="kyjqwtu2dziy">
                    {isSystem && <span className="mr-1 text-xs font-medium text-white/60" data-id="element-97" data-mp="70ldprrhc951">from</span>}
                    {formatPrice(match.price)}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
            {isSystem ? <Link to={`/product/${match.id}`} className="mt-auto flex h-11 w-full items-center justify-center rounded-lg bg-volt pt-0 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-98" data-mp="3tdjufpyufp9f">
                Configure your system
              </Link> : <button type="button" onClick={() => addItem(match)} className="mt-auto h-11 w-full rounded-lg bg-volt text-sm font-semibold text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-99" data-mp="3ag908js2jm0u">
                Add to cart
              </button>}
          </div>
        </div>
      </div>
    </section>;
}